import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, Collapse, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import KeyboardReturnRoundedIcon from "@mui/icons-material/KeyboardReturnRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import dayjs from "dayjs";
import { isPreviewable } from "../../../component/common/AttachmentPreviewList";
import AttachmentPreviewModal from "../../mail/components/AttachmentPreviewModal";
import { workRequestApi } from "../api/workRequestApi";
import { runApiAction } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { formatFileSize } from "../../../util/numberFormat";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

const REQ = { label: "요청", accent: C.primary, bg: C.primaryBg, fg: C.primaryText };
const RES = { label: "응답", accent: "#16a34a", bg: "#f0fdf4", fg: "#15803d" };

/**
 * 리비전 행들을 <b>버전(=왕복) 단위</b>로 묶고, 코멘트를 그 버전·작성자 쪽에 귀속시킨다.
 *
 * <p>서버는 한 버전을 요청/응답 <b>두 행</b>으로 내려준다(같은 version, 다른 side).
 * 화면에서는 그 둘이 좌우 한 쌍이어야 "이 요청에 이렇게 답했다" 가 한눈에 읽힌다.
 *
 * <p>코멘트 귀속은 {@code version} 필드가 1순위(각 버전 박스 입력창에서 쓴 값).
 * 그 컬럼이 없던 시절의 레거시 행만 작성 시각으로 폴백한다.
 *
 * @returns {Array<{version, request, delivery, comments}>} 버전 오름차순(오래된 → 최신)
 */
export function buildTimeline(revisions = [], comments = []) {
    const byVersion = new Map();
    for (const r of revisions) {
        const v = r.version || 0;
        if (!byVersion.has(v)) byVersion.set(v, { version: v, request: null, delivery: null, comments: [] });
        const bucket = byVersion.get(v);
        if (r.side === "DELIVERY") bucket.delivery = r;
        else bucket.request = r;
    }
    const rounds = [...byVersion.values()].sort((a, b) => a.version - b.version);
    if (!rounds.length) return [];

    /** 그 왕복이 시작된 시각 — 요청이 없으면(이상 데이터) 응답 시각으로 폴백. */
    const startedAt = (r) => new Date((r.request || r.delivery)?.createdAt || 0).getTime();
    const indexOfVersion = new Map(rounds.map((r, i) => [r.version, i]));

    for (const c of comments) {
        let idx = indexOfVersion.get(c.version);
        if (idx === undefined) {
            idx = 0;
            const t = new Date(c.createdAt || 0).getTime();
            for (let i = 0; i < rounds.length; i++) {
                if (startedAt(rounds[i]) <= t) idx = i;
                else break;
            }
        }
        rounds[idx].comments.push(c);
    }
    return rounds;
}

/**
 * 진행 타임라인 — <b>버전 하나가 박스 하나</b>. 그 안에 좌:요청 / 우:응답, 그리고 입력창까지 들어간다.
 *
 * <p>입력창이 박스 <b>안에</b> 있는 이유: 밖에 두면 "지금 쓰는 게 어느 버전에 달리는지" 가 안 보인다.
 * 최신 박스에만 입력창이 뜨고, 무엇이 일어날지를 그 자리에서 문구로 알려준다.
 *
 * <p><b>언제 새 버전이 생기나</b> — 요청자가 <u>응답을 받은 뒤</u> 다시 보낼 때만이다.
 * 응답이 오기 전에 보내는 건 같은 버전에 덧붙는 '참고' 다. 담당자가 보내는 건 언제나 그 버전의 응답.
 */
export default function WorkRequestTimeline({
    requestId, revisions = [], comments = [], myId, requesterId,
    isAssignee = false, canWrite = false, onChanged, sx,
}) {
    const rounds = buildTimeline(revisions, comments);

    if (!rounds.length) {
        return (
            <Typography sx={{ fontSize: 12.5, color: C.textMuted, ...sx }}>
                아직 등록된 내용이 없습니다.
            </Typography>
        );
    }

    const removeComment = async (commentId) => {
        const ok = await confirmDialog({
            message: "이 내용을 삭제할까요?", confirmLabel: "삭제", variant: "danger",
        });
        if (!ok) return;
        runApiAction({
            request: () => workRequestApi.removeComment(commentId),
            successMsg: "삭제했습니다.",
            onSuccess: () => onChanged?.(),
        });
    };

    return (
        <Stack spacing={1.5} sx={sx}>
            {rounds.map((round, i) => (
                <RoundBox
                    key={round.version}
                    requestId={requestId}
                    round={round}
                    isLatest={i === rounds.length - 1}
                    myId={myId}
                    requesterId={requesterId}
                    isAssignee={isAssignee}
                    canWrite={canWrite}
                    onRemoveComment={removeComment}
                    onChanged={onChanged}
                />
            ))}
        </Stack>
    );
}

/** 왕복 1회 = 박스 1개. 접을 수 있고, 최신 박스에만 입력창이 붙는다. */
function RoundBox({
    requestId, round, isLatest, myId, requesterId, isAssignee, canWrite, onRemoveComment, onChanged,
}) {
    const { version, request, delivery, comments } = round;
    // 지난 버전은 접어둔다 — 왕복이 쌓여도 최신이 화면 안에 남게.
    const [open, setOpen] = useState(isLatest);
    useEffect(() => { setOpen(isLatest); }, [isLatest]);

    const reqComments = comments.filter(c => c.authorId === requesterId);
    const resComments = comments.filter(c => c.authorId !== requesterId);
    const fileCount = (request?.attachments?.length || 0) + (delivery?.attachments?.length || 0);

    return (
        <Box sx={{
            borderRadius: 2, overflow: "hidden",
            border: `1px solid ${isLatest ? C.primaryBorder : C.border}`,
            bgcolor: "#fff",
            ...(isLatest ? { boxShadow: "0 2px 12px rgba(15,23,42,0.06)" } : {}),
        }}>
            {/* 머리 — 버전 배지는 좌측 상단, 우측은 접기 토글 */}
            <Stack
                direction="row" alignItems="center" spacing={1}
                onClick={() => setOpen(o => !o)}
                sx={{
                    px: 1.5, py: 1, cursor: "pointer",
                    bgcolor: isLatest ? C.primaryBg : "#f8fafc",
                    "&:hover": { bgcolor: isLatest ? "#e7f0ff" : "#f1f5f9" },
                }}
            >
                <Chip label={`v${version}`} size="small" sx={{
                    height: 21, fontSize: 11.5, fontWeight: 800,
                    bgcolor: isLatest ? C.primary : "#e2e8f0",
                    color: isLatest ? "#fff" : C.textSecondary,
                }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: delivery ? RES.fg : C.textMuted }}>
                    {delivery ? "응답 완료" : "응답 대기 중"}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }} />
                {!open && fileCount > 0 && (
                    <Typography sx={{ fontSize: 11, color: C.textMuted }}>파일 {fileCount}</Typography>
                )}
                <ExpandMoreRoundedIcon sx={{
                    fontSize: 19, color: C.textMuted,
                    transform: open ? "rotate(180deg)" : "none", transition: "transform .15s",
                }} />
            </Stack>

            <Collapse in={open} unmountOnExit>
                {/* 좌: 요청 / 우: 응답 — 세로줄 하나로만 가른다(선을 더 긋지 않는다).
                    입력창도 각 칸 <b>안쪽 아래</b>에 달아, 내가 쓰는 칸이 어디인지 위치로 보이게 한다. */}
                <Box sx={{
                    display: "grid", gridTemplateColumns: "1fr 1fr",
                    borderTop: `1px solid ${C.border}`, alignItems: "stretch",
                }}>
                    <SideCell
                        meta={REQ} rev={request} comments={reqComments}
                        emptyText="요청 내용이 없습니다."
                        myId={myId} onRemoveComment={onRemoveComment}
                        // 내 공간만 흰 배경 — 상대 칸은 회색으로 눌러 "여기는 내가 쓰는 곳" 이 한눈에.
                        mine={canWrite && !isAssignee}
                        composer={isLatest && canWrite ? {
                            requestId,
                            // 요청 칸은 요청자만 — 담당자에게는 보이되 잠긴다(대칭이 보여야 구조가 읽힌다).
                            active: !isAssignee,
                            placeholder: "의견을 적거나 파일을 첨부하세요",
                            onDone: onChanged,
                        } : null}
                    />
                    <SideCell
                        meta={RES} rev={delivery} comments={resComments}
                        emptyText="아직 응답이 없습니다."
                        myId={myId} onRemoveComment={onRemoveComment} borderLeft
                        mine={canWrite && isAssignee}
                        composer={isLatest && canWrite ? {
                            requestId,
                            active: isAssignee,
                            placeholder: "응답 내용을 적거나 파일을 첨부하세요",
                            onDone: onChanged,
                        } : null}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}

/**
 * 한쪽 칸 — 보낸 사람·시각 → 내용 → 파일 → 그 쪽에서 남긴 말 → <b>그 쪽 입력창</b>, 세로로 흐른다.
 *
 * <p>입력창을 칸 안쪽 아래에 두는 이유: 요청/응답 중 <b>내가 쓰는 칸이 어디인지</b>가 위치로 보인다.
 * 상대 칸의 입력창은 지우지 않고 잠가둔다 — 사라지면 왜 못 쓰는지 알 수 없다.
 */
function SideCell({
    meta, rev, comments, emptyText, myId, onRemoveComment, composer, mine = false, borderLeft = false,
}) {
    const slots = groupBySlot(rev?.attachments || []);
    const empty = !rev && !comments.length;
    // 파일과 말은 성격이 다르니 영역을 나눈다 — 섞이면 뭐가 파일이고 뭐가 의견인지 훑기 어렵다.
    const notes = rev?.note ? [{ id: `note-${rev.id}`, content: rev.note, createdAt: rev.createdAt }] : [];
    const lines = [...notes, ...comments];

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", minWidth: 0,
            // 내 공간이 아니면 회색 — 어디에 쓰면 되는지가 색으로 먼저 읽힌다.
            bgcolor: mine ? "#fff" : "#f8fafc",
            ...(borderLeft ? { borderLeft: `1px solid ${C.border}` } : {}),
        }}>
            <Box sx={{ flex: 1, px: 1.5, py: 1.25 }}>
                {empty && (
                    <Typography sx={{ fontSize: 12, color: C.textMuted }}>{emptyText}</Typography>
                )}

                {rev && (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.9 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: meta.accent, flexShrink: 0 }} />
                        <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 700, color: C.textPrimary, minWidth: 0 }}>
                            {rev.authorName}
                        </Typography>
                    </Stack>
                )}

                {/* ① 파일 — 카드가 아니라 한 줄씩. 개수를 머리에 달아 몇 개인지 세지 않아도 되게. */}
                {rev?.attachments?.length > 0 && (
                    <Box sx={{ mb: lines.length ? 1.25 : 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, mb: 0.4 }}>
                            첨부파일 ({rev.attachments.length})
                        </Typography>
                        <AttachmentLines items={slots.flatMap(s => s.items.map(a => ({ ...a, slot: s.slot })))} />
                    </Box>
                )}

                {/* ② 말 — 첨부파일과 같은 머리·들여쓰기로 맞춰 두 묶음이 나란히 읽히게 한다.
                    내용과 시각만 남긴다(이름은 칸 위에 이미 있으니 줄마다 반복하지 않는다). */}
                {lines.length > 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, mb: 0.4 }}>
                            댓글 ({lines.length})
                        </Typography>
                        <Stack spacing={0.1}>
                            {lines.map(c => (
                                <CommentLine
                                    key={c.id}
                                    comment={c}
                                    mine={c.authorId === myId}
                                    onRemove={c.authorId != null ? () => onRemoveComment(c.id) : undefined}
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>

            {composer && <SideComposer meta={meta} {...composer} />}
        </Box>
    );
}

/**
 * 첨부 목록 — <b>한 줄에 한 파일</b>. 파일명이 주인공이고 크기·시각은 뒤로 물린다.
 *
 * <p>공용 {@code AttachmentPreviewList} 는 카드형이라 파일이 3~8개면 칸을 통째로 잡아먹는다.
 * 여기는 좌우 2단 안이라 세로 공간이 귀해서 줄 단위로 압축했다(다운로드·미리보기는 그대로).
 *
 * <p>{@code slot} 은 그 파일이 채운 양식 이름 — 요청자가 파일명을 자유롭게 짓기 때문에
 * 이름만으로는 어느 양식 자리인지 안 보인다. 다만 <b>이름이 같으면 중복이라 생략</b>한다.
 */
function AttachmentLines({ items = [] }) {
    const [preview, setPreview] = useState(null);

    return (
        <>
            <Stack spacing={0.1}>
                {items.map(a => {
                    const showSlot = a.slot && a.slot !== a.originalFileName;
                    return (
                        <Stack
                            key={a.id}
                            direction="row" alignItems="center" spacing={0.75}
                            sx={{
                                py: 0.4, px: 0.6, borderRadius: 1, minWidth: 0,
                                "&:hover": { bgcolor: "rgba(15,23,42,0.035)" },
                            }}
                        >
                            <InsertDriveFileRoundedIcon sx={{ fontSize: 14, color: C.textMuted, flexShrink: 0 }} />
                            <Typography noWrap sx={{ flex: 1, fontSize: 12.5, color: C.textPrimary, minWidth: 0 }}>
                                {showSlot && (
                                    <Box component="span" sx={{ color: C.textMuted, fontSize: 11, mr: 0.5 }}>
                                        [{a.slot}]
                                    </Box>
                                )}
                                {a.originalFileName}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: C.textMuted, flexShrink: 0, whiteSpace: "nowrap" }}>
                                {formatFileSize(a.fileSize)}
                                {a.uploadedAt ? ` · ${dayjs(a.uploadedAt).format("MM.DD HH:mm")}` : ""}
                            </Typography>
                            {isPreviewable(a) && (
                                <Tooltip title="미리보기">
                                    <IconButton
                                        size="small"
                                        onClick={() => setPreview({
                                            attachment: { id: a.id, filename: a.originalFileName, mimeType: a.contentType },
                                            loader: () => workRequestApi.previewAttachment(a.id),
                                        })}
                                        sx={{ p: 0.25, flexShrink: 0 }}
                                    >
                                        <ZoomInRoundedIcon sx={{ fontSize: 15, color: C.textSecondary }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="다운로드">
                                {/* downloadUrl = Content-Disposition:attachment 가 박힌 presigned URL.
                                    fileUrl 을 쓰면 S3 가 cross-origin 이라 다운로드 대신 새 탭이 열린다.

                                    target="_blank" 는 빼면 안 된다 — 없으면 지금 문서를 그 URL 로
                                    이동시키는 것이 되고, 다운로드 저장을 한 번 취소하면 그 뒤 클릭이
                                    조용히 무시돼 새로고침해야 다시 받아진다(게시판에서 겪은 증상). */}
                                <IconButton size="small" component="a" href={a.downloadUrl || a.fileUrl}
                                            target="_blank" rel="noopener noreferrer"
                                            sx={{ p: 0.25, flexShrink: 0 }}>
                                    <DownloadRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    );
                })}
            </Stack>
            {preview && (
                <AttachmentPreviewModal
                    open
                    attachment={preview.attachment}
                    loader={preview.loader}
                    onClose={() => setPreview(null)}
                />
            )}
        </>
    );
}

/** 말 한 줄 — <b>내용 · 시각</b>만. 박스도 배경도 없다(줄마다 상자를 씌우면 화면이 시끄럽다). */
function CommentLine({ comment: c, mine, onRemove }) {
    return (
        <Stack
            direction="row" alignItems="baseline" spacing={0.75}
            sx={{
                py: 0.4, px: 0.6, borderRadius: 1, minWidth: 0,
                "&:hover": { bgcolor: "rgba(15,23,42,0.035)" },
                "&:hover .cmt-del": { opacity: 1 },
            }}
        >
            <Typography sx={{
                flex: 1, minWidth: 0, fontSize: 12.5, whiteSpace: "pre-wrap",
                color: c.systemNote ? "#b45309" : C.textPrimary,
            }}>
                {c.content}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: C.textMuted, flexShrink: 0 }}>
                {dayjs(c.createdAt).format("MM.DD HH:mm")}
            </Typography>
            {!c.systemNote && mine && onRemove && (
                <Tooltip title="삭제">
                    <IconButton size="small" className="cmt-del" onClick={onRemove}
                                sx={{ p: 0.1, opacity: 0, transition: "opacity .15s", flexShrink: 0 }}>
                        <DeleteRoundedIcon sx={{ fontSize: 12, color: "#ef4444" }} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
}

/**
 * 칸 안쪽 아래에 붙는 입력창 — 글·파일을 함께 보낸다.
 *
 * <p>{@code active} 가 false 면 잠긴 채로 남는다(숨기지 않는다). 상대 칸에도 입력 자리가 보여야
 * "저쪽은 담당자가 쓰는 곳" 이라는 구조가 읽히고, 왜 내가 못 쓰는지도 문구로 바로 알 수 있다.
 *
 * <p>무엇이 일어날지({@code hint})는 호출부가 정한다 — 새 버전이 열리는 조건은 서버가 판정하므로
 * 여기 문구는 안내일 뿐이다.
 */
function SideComposer({ meta, requestId, active, placeholder, onDone }) {
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const addFiles = (picked) => {
        // 같은 파일을 두 번 고르면 중복 전송되므로 (name, size) 로 거른다.
        const seen = new Set(files.map(f => `${f.name}:${f.size}`));
        const next = [...files];
        for (const f of picked) {
            const key = `${f.name}:${f.size}`;
            if (!seen.has(key)) { seen.add(key); next.push(f); }
        }
        setFiles(next);
    };

    const send = () => {
        const body = text.trim();
        if (!active || busy || (!body && !files.length)) return;
        runApiAction({
            request: () => workRequestApi.addRevision(requestId, body || null, files),
            setLoading: setBusy,
            onSuccess: () => { setText(""); setFiles([]); onDone?.(); },
        });
    };

    const canSend = active && !busy && (text.trim() || files.length > 0);

    return (
        <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${C.border}` }}>
            <input
                ref={inputRef} type="file" hidden multiple
                onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
            />

            {files.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 0.75 }}>
                    {files.map((f, i) => (
                        <Chip
                            key={`${f.name}-${f.size}-${i}`}
                            icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 13 }} />}
                            label={`${f.name} (${formatFileSize(f.size)})`}
                            size="small"
                            onDelete={() => setFiles(files.filter((_, idx) => idx !== i))}
                            sx={{ maxWidth: "100%", fontSize: 11, bgcolor: meta.bg, border: `1px solid ${C.border}` }}
                        />
                    ))}
                </Box>
            )}

            {/* 첨부·전송을 입력칸 <b>안쪽</b>에 넣어 버튼이 밖으로 새지 않게 한다.
                전송은 종이비행기 대신 엔터 화살표 — Enter 로 보낸다는 게 아이콘으로 바로 읽힌다. */}
            <TextField
                fullWidth multiline maxRows={4} size="small"
                value={text}
                disabled={!active}
                onChange={(e) => setText(e.target.value)}
                placeholder={placeholder}
                sx={{ bgcolor: active ? "#fff" : "transparent", borderRadius: 1 }}
                onKeyDown={(e) => {
                    // Enter=전송, Shift+Enter=줄바꿈. IME 조합 중 Enter 는 무시(한글 입력 중 오전송 방지).
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        send();
                    }
                }}
                InputProps={{
                    sx: { alignItems: "flex-end", py: 0.5, pl: 0.75, pr: 0.75 },
                    startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0.5, mb: 0.25 }}>
                            <IconButton
                                size="small" disabled={!active}
                                onClick={() => inputRef.current?.click()}
                                sx={{ p: 0.4, color: C.textSecondary }}
                            >
                                <AttachFileRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end" sx={{ ml: 0.5, mb: 0.25 }}>
                            <IconButton
                                size="small" onClick={send} disabled={!canSend}
                                sx={{
                                    p: 0.4, borderRadius: 1.2,
                                    color: canSend ? meta.accent : "#cbd5e1",
                                    "&:hover": { bgcolor: meta.bg },
                                }}
                            >
                                <KeyboardReturnRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
}

/**
 * 첨부를 '채운 양식 슬롯' 단위로 묶는다 — 최초 요청본은 양식마다 한 칸씩 채운 것이라
 * 어느 양식 자리인지 보여줘야 읽힌다(파일명은 요청자가 자유롭게 짓기 때문).
 */
function groupBySlot(attachments) {
    const order = [];
    const bySlot = new Map();
    const free = [];
    for (const a of attachments) {
        if (!a.sourceFormatName) { free.push(a); continue; }
        if (!bySlot.has(a.sourceFormatName)) { bySlot.set(a.sourceFormatName, []); order.push(a.sourceFormatName); }
        bySlot.get(a.sourceFormatName).push(a);
    }
    const out = order.map(slot => ({ slot, items: bySlot.get(slot) }));
    if (free.length) out.push({ slot: null, items: free });
    return out;
}
